(() => {
  const root = document.documentElement;
  const body = document.body;
  const config = window.DesiAttarTheme || {};

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const escapeHTML = (value = '') =>
    String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    })[character]);

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return (config.moneyFormat || '₹{{amount}}')
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(Number(cents || 0) / 100)))
      .replace('{{ amount_no_decimals }}', String(Math.round(Number(cents || 0) / 100)));
  };

  const toast = (message) => {
    const element = qs('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    window.clearTimeout(element._hideTimer);
    element._hideTimer = window.setTimeout(() => element.classList.remove('is-visible'), 2600);
  };

  const overlay = qs('[data-site-overlay]');
  const closePanels = () => {
    qsa('[data-cart-drawer].is-open, [data-mobile-menu].is-open, [data-search-panel].is-open').forEach((element) => {
      element.classList.remove('is-open');
      element.setAttribute('aria-hidden', 'true');
    });
    overlay?.classList.remove('is-visible');
    root.classList.remove('is-locked');
  };

  const openPanel = (element) => {
    closePanels();
    if (!element) return;
    element.classList.add('is-open');
    element.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('is-visible');
    root.classList.add('is-locked');
  };

  const cartDrawer = qs('[data-cart-drawer]');
  const mobileMenu = qs('[data-mobile-menu]');
  const searchPanel = qs('[data-search-panel]');

  qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', () => openPanel(cartDrawer)));
  qsa('[data-cart-close], [data-mobile-menu-close], [data-search-close]').forEach((button) => button.addEventListener('click', closePanels));
  qsa('[data-mobile-menu-open]').forEach((button) => button.addEventListener('click', () => openPanel(mobileMenu)));
  qsa('[data-search-open]').forEach((button) => button.addEventListener('click', () => {
    openPanel(searchPanel);
    window.setTimeout(() => qs('input[type="search"]', searchPanel)?.focus(), 180);
  }));
  overlay?.addEventListener('click', closePanels);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePanels();
  });

  const header = qs('[data-site-header]');
  const announcement = qs('[data-announcement]');
  const updateHeader = () => {
    header?.classList.toggle('is-scrolled', window.scrollY > 24);
    root.style.setProperty('--announcement-height', `${announcement?.offsetHeight || 0}px`);
  };
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
  window.addEventListener('resize', updateHeader, { passive: true });

  const cartImage = (item) => item.featured_image?.url || item.image || '';
  const resizedCartImage = (item) => {
    const source = cartImage(item);
    if (!source) return '';
    return `${source}${source.includes('?') ? '&' : '?'}width=240`;
  };
  const renderCart = (cart) => {
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });

    const itemContainer = qs('[data-cart-drawer-items]');
    const emptyState = qs('[data-cart-empty]');
    const footer = qs('[data-cart-drawer-footer]');
    const subtotal = qs('[data-cart-subtotal]');

    if (!itemContainer) return;
    itemContainer.innerHTML = cart.items.map((item, index) => `
      <article class="drawer-cart-item" data-line="${index + 1}">
        <a class="drawer-cart-item__image" href="${escapeHTML(item.url)}">
          ${cartImage(item) ? `<img src="${escapeHTML(resizedCartImage(item))}" alt="${escapeHTML(item.product_title)}" width="120" height="150">` : ''}
        </a>
        <div class="drawer-cart-item__details">
          <a class="drawer-cart-item__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : ''}
          <span>${formatMoney(item.final_line_price)}</span>
          <div class="quantity quantity--small">
            <button type="button" data-cart-quantity="${index + 1}" data-value="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-quantity="${index + 1}" data-value="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="drawer-cart-item__remove" type="button" data-cart-quantity="${index + 1}" data-value="0" aria-label="Remove ${escapeHTML(item.product_title)}">×</button>
      </article>
    `).join('');

    emptyState?.toggleAttribute('hidden', cart.item_count > 0);
    footer?.toggleAttribute('hidden', cart.item_count === 0);
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  };

  const getCart = async () => {
    const response = await fetch(config.cartJsonUrl || '/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const refreshCart = async (open = false) => {
    const cart = await getCart();
    renderCart(cart);
    if (open) openPanel(cartDrawer);
    return cart;
  };

  document.addEventListener('click', async (event) => {
    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (!quantityButton) return;
    quantityButton.disabled = true;
    try {
      const response = await fetch(config.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(quantityButton.dataset.cartQuantity), quantity: Number(quantityButton.dataset.value) })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      renderCart(await response.json());
    } catch (error) {
      toast(error.message);
    } finally {
      quantityButton.disabled = false;
    }
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || form.dataset.nativeSubmit === 'true') return;
    event.preventDefault();

    const submitButton = qs('[type="submit"]', form);
    const initialText = submitButton?.textContent;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding…';
    }

    try {
      const response = await fetch(config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add this item');
      await refreshCart(true);
      toast('Added to your cart');
    } catch (error) {
      toast(error.message);
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = initialText;
      }
    }
  });
  const initProduct = (scope = document) => {
    qsa('[data-product-root]', scope).forEach((productRoot) => {
      if (productRoot.dataset.ready === 'true') return;
      productRoot.dataset.ready = 'true';
      const variantInput = qs('[name="id"]', productRoot);
      const price = qs('[data-product-price]', productRoot);
      const comparePrice = qs('[data-product-compare-price]', productRoot);
      const addButton = qs('[data-add-to-cart]', productRoot);

      qsa('[data-variant-button]', productRoot).forEach((button) => {
        button.addEventListener('click', () => {
          qsa('[data-variant-button]', productRoot).forEach((item) => item.classList.remove('is-selected'));
          button.classList.add('is-selected');
          variantInput.value = button.dataset.variantId;
          if (price) price.textContent = button.dataset.price;
          if (comparePrice) {
            comparePrice.textContent = button.dataset.comparePrice || '';
            comparePrice.hidden = !button.dataset.comparePrice;
          }
          if (addButton) {
            const available = button.dataset.available === 'true';
            addButton.disabled = !available;
            addButton.textContent = available ? addButton.dataset.availableText : addButton.dataset.soldText;
          }
          const mediaId = button.dataset.mediaId;
          if (mediaId) qs(`[data-media-id="${mediaId}"]`, productRoot)?.click();
        });
      });

      qsa('[data-media-id]', productRoot).forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          const target = qs(`[data-product-media="${thumbnail.dataset.mediaId}"]`, productRoot);
          if (!target) return;
          qsa('[data-media-id]', productRoot).forEach((item) => item.classList.remove('is-active'));
          thumbnail.classList.add('is-active');
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });
      });
    });
  };

  const initSpotlights = (scope = document) => {
    qsa('[data-spotlight]', scope).forEach((hero) => {
      if (hero.dataset.ready === 'true') return;
      const canvas = qs('canvas', hero);
      const reveal = qs('[data-spotlight-reveal]', hero);
      const context = canvas?.getContext('2d');
      if (!canvas || !reveal || !context) return;
      hero.dataset.ready = 'true';

      const radius = Number(hero.dataset.radius || 260);
      const mouse = { x: -999, y: -999 };
      const smooth = { x: -999, y: -999 };
      let frame = null;
      let active = false;

      const resize = () => {
        canvas.width = Math.max(1, Math.round(hero.clientWidth));
        canvas.height = Math.max(1, Math.round(hero.clientHeight));
      };

      const draw = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        const gradient = context.createRadialGradient(smooth.x, smooth.y, 0, smooth.x, smooth.y, radius);
        gradient.addColorStop(0, 'rgba(255,255,255,1)');
        gradient.addColorStop(0.4, 'rgba(255,255,255,1)');
        gradient.addColorStop(0.6, 'rgba(255,255,255,.75)');
        gradient.addColorStop(0.75, 'rgba(255,255,255,.4)');
        gradient.addColorStop(0.88, 'rgba(255,255,255,.12)');
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        context.beginPath();
        context.arc(smooth.x, smooth.y, radius, 0, Math.PI * 2);
        context.fillStyle = gradient;
        context.fill();
        const mask = `url(${canvas.toDataURL('image/png')})`;
        reveal.style.webkitMaskImage = mask;
        reveal.style.maskImage = mask;
      };

      const animate = () => {
        smooth.x += (mouse.x - smooth.x) * 0.1;
        smooth.y += (mouse.y - smooth.y) * 0.1;
        draw();
        if (active || Math.abs(mouse.x - smooth.x) > 0.25 || Math.abs(mouse.y - smooth.y) > 0.25) {
          frame = requestAnimationFrame(animate);
        } else {
          frame = null;
        }
      };

      const move = (event) => {
        const rect = hero.getBoundingClientRect();
        mouse.x = event.clientX - rect.left;
        mouse.y = event.clientY - rect.top;
        if (smooth.x < -500) Object.assign(smooth, mouse);
        hero.classList.add('is-active');
        active = true;
        if (frame === null) frame = requestAnimationFrame(animate);
      };

      const leave = () => {
        active = false;
        hero.classList.remove('is-active');
      };

      resize();
      hero.addEventListener('pointerenter', move);
      hero.addEventListener('pointermove', move, { passive: true });
      hero.addEventListener('pointerleave', leave);
      window.addEventListener('resize', resize, { passive: true });
      hero._spotlightCleanup = () => {
        active = false;
        if (frame !== null) cancelAnimationFrame(frame);
        hero.removeEventListener('pointerenter', move);
        hero.removeEventListener('pointermove', move);
        hero.removeEventListener('pointerleave', leave);
        window.removeEventListener('resize', resize);
      };
    });
  };

  const initAccordions = (scope = document) => {
    qsa('[data-accordion-button]', scope).forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('.accordion');
        const expanded = item.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(expanded));
      });
    });
  };

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      }), { threshold: 0.12 })
    : null;

  const initReveal = (scope = document) => {
    qsa('[data-reveal]', scope).forEach((element) => revealObserver ? revealObserver.observe(element) : element.classList.add('is-visible'));
  };

  initProduct();
  initSpotlights();
  initAccordions();
  initReveal();
  refreshCart().catch(() => {});

  document.addEventListener('shopify:section:load', (event) => {
    initProduct(event.target);
    initSpotlights(event.target);
    initAccordions(event.target);
    initReveal(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    qsa('[data-spotlight]', event.target).forEach((hero) => hero._spotlightCleanup?.());
  });
})();
